import { useMemo } from 'react';

import { EyeIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { textWithReferences } from 'helpers/form';
import { paramChipHtmlToMustachePlaceholders } from 'helpers/param-chip-markup';
import { CustomButton } from 'views/CandidateApp/components/CustomButton/CustomButton';

const DEFAULT_EMAIL_TEMPLATE_PREVIEW_TEXT_PARAMS = {
  candidateFirstName: '{{candidateFirstName}}',
  candidateFullName: '{{candidateFullName}}',
  candidateLastName: '{{candidateLastName}}',
  job: '{{job}}',
  company: '{{company}}',
  refereeFirstName: '{{refereeFirstName}}',
  refereeFullName: '{{refereeFullName}}',
  refereeLastName: '{{refereeLastName}}',
};

export const EmailTemplatePreview = ({
  brand,
  subject,
  body,
  showSupportFooter = false,
  showActions = false,
  className,
  showPreviewWarning = true,
  textParams = DEFAULT_EMAIL_TEMPLATE_PREVIEW_TEXT_PARAMS,
}) => {
  const logoAlignmentClass = useMemo(() => {
    switch (brand?.email_logo_alignment) {
      case 'left':
        return 'justify-start';
      case 'center':
        return 'justify-center';
      default:
        return 'justify-end';
    }
  }, [brand?.email_logo_alignment]);

  const renderContent = text => {
    const normalized = paramChipHtmlToMustachePlaceholders(text ?? '');
    return textWithReferences(normalized, textParams, { previewMode: true }, false);
  };

  return (
    <div
      className={cn(
        'flex h-full min-h-0 flex-col gap-4 rounded-3xl border border-neutral-4 shadow-sm overflow-hidden border border-neutral-4 bg-yellow-2/30',
        className,
      )}>
      {showPreviewWarning && (
        <div className="flex flex-wrap items-center justify-between gap-2 flex-shrink-0 px-8 py-4 !pb-0">
          <span className="text-xl text-orange-3 font-medium flex items-center gap-2">
            <HugeiconsIcon icon={EyeIcon} size="16" className="text-orange-3" /> Preview only - Not
            Interactive
          </span>
          <span className="text-lg text-orange-3 font-regular">
            This is a preview. Buttons and inputs do not work.
          </span>
        </div>
      )}
      <div className="flex flex-col gap-4 p-8 bg-[#f2f4f6] rounded-tl-3xl rounded-tr-3xl border-t border-neutral-4 h-full cursor-not-allowed">
        <div className="flex flex-col gap-4 rounded-3xl border border-neutral-4 bg-neutral-1 p-8 shadow-sm">
          <span
            className="text-xl font-medium text-neutral-7 pointer-events-none"
            dangerouslySetInnerHTML={{ __html: renderContent(subject) }}
          />
        </div>

        {!brand?.banner_logo && brand?.brand_logo && (
          <div className={cn('flex', logoAlignmentClass)}>
            <img src={brand.brand_logo} alt="Brand Logo" className="max-h-[70px] max-w-[200px]" />
          </div>
        )}
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto rounded-3xl border border-neutral-4 bg-neutral-1 shadow-sm">
          {brand?.banner_logo && (
            <div className="flex justify-center">
              <img src={brand.banner_logo} alt="Email banner" className="w-full object-contain" />
            </div>
          )}
          <div className="p-8 flex flex-col gap-4 pointer-events-none">
            <div
              className="html-embedded-lists primary-text"
              dangerouslySetInnerHTML={{ __html: renderContent(body) }}
            />
            {showActions && (
              <div className="mt-6 flex flex-col gap-4">
                <CustomButton brand={brand} type="primary" className="max-w-max" onClick={() => {}}>
                  Action Button
                </CustomButton>
              </div>
            )}
            {showSupportFooter && (
              <div className="mt-6 flex flex-col gap-4">
                <p className="text-2xl font-medium text-neutral-7">Need Help?</p>
                <p className="text-xl text-neutral-7">
                  Email us at{' '}
                  <a href="#" style={{ color: brand?.color_link }}>
                    team@checkmate.tech
                  </a>{' '}
                  or{' '}
                  <a href="#" style={{ color: brand?.color_link }}>
                    contact us
                  </a>
                  <br />
                  Checkmate – Reference Checking Made Easy.{' '}
                  <a href="#" style={{ color: brand?.color_link }}>
                    Learn more
                  </a>
                  <br />
                  <a href="#" style={{ color: brand?.color_link }}>
                    Terms and Conditions
                  </a>
                </p>
                <p>
                  Thanks,
                  <br />
                  The Checkmate Team
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
